import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import DiscographyPage from "@/pages/DiscographyPage";
import VideosPage from "@/pages/VideosPage";
import BlogPage from "@/pages/BlogPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import PhotosPage from "@/pages/PhotosPage";
import AboutPage from "@/pages/AboutPage";
import AdminPage from "@/pages/AdminPage";
import SecretPage from "@/pages/SecretPage";

// Components
import Taskbar from "@/components/Taskbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Page title mapping
const PAGE_TITLES = {
  '/': 'root',
  '/discography': 'Discography',
  '/videos': 'Videos',
  '/blog': 'Blog',
  '/photos': 'Photos',
  '/about': 'About',
  '/admin': 'Admin',
  '/holly': 'holly'
};

// Pages where taskbar should be hidden
const HIDE_TASKBAR_PATHS = ['/holly'];

// Component to handle dynamic page titles
function TitleUpdater() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    // Check for exact match first, then check if it starts with /blog/ for blog details
    let title = PAGE_TITLES[path];
    if (!title && path.startsWith('/blog/')) {
      title = 'Blog';
    }
    document.title = title || 'root';
  }, [location]);
  
  return null;
}

// Component to conditionally render taskbar
function ConditionalTaskbar() {
  const location = useLocation();
  const shouldHide = HIDE_TASKBAR_PATHS.includes(location.pathname);
  
  if (shouldHide) return null;
  return <Taskbar />;
}

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await fetch(`${API}/seed`, { method: 'POST' });
      } catch (e) {
        console.log('Seed may have already run or failed:', e);
      }
    };
    seedData();
  }, []);

  // Fetch and apply site settings (colors/fonts)
  useEffect(() => {
    const applySettings = async () => {
      try {
        const res = await fetch(`${API}/settings`);
        if (res.ok) {
          const settings = await res.json();
          const root = document.documentElement;
          
          // Apply color settings to CSS variables
          if (settings.bg_body) {
            root.style.setProperty('--bg-body', settings.bg_body);
            document.body.style.backgroundColor = settings.bg_body;
          }
          if (settings.bg_container) root.style.setProperty('--bg-container', settings.bg_container);
          if (settings.bg_section) root.style.setProperty('--bg-section', settings.bg_section);
          if (settings.accent_orange) root.style.setProperty('--accent-orange', settings.accent_orange);
          if (settings.accent_cyan) root.style.setProperty('--accent-cyan', settings.accent_cyan);
          if (settings.text_white) {
            root.style.setProperty('--text-white', settings.text_white);
            document.body.style.color = settings.text_white;
          }
          if (settings.text_link) root.style.setProperty('--text-link', settings.text_link);
          if (settings.border_color) root.style.setProperty('--border-color', settings.border_color);
          
          // Apply background image if set
          if (settings.bg_image) {
            document.body.style.backgroundImage = `url(${settings.bg_image})`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundPosition = 'center';
            document.body.style.backgroundAttachment = 'fixed';
          } else {
            document.body.style.backgroundImage = 'none';
          }
          
          // Apply font settings
          if (settings.font_heading) {
            root.style.setProperty('--font-heading', settings.font_heading);
          }
          if (settings.font_body) {
            document.body.style.fontFamily = `${settings.font_body}, Arial, sans-serif`;
            root.style.setProperty('--font-body', settings.font_body);
          }
          if (settings.font_nav) {
            root.style.setProperty('--font-nav', settings.font_nav);
          }
        }
      } catch (e) {
        console.log('Failed to load settings:', e);
      }
    };
    applySettings();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <TitleUpdater />
        <ConditionalTaskbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/discography" element={<DiscographyPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetailPage />} />
          <Route path="/photos" element={<PhotosPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/holly" element={<SecretPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
